/** Personal email sign-off (Collections lead, 9 Oct 2026). Added under every inbox reply / forward / new email and Customer File
 *  emails — the company signature itself comes from CodeTwo, so this is just the person: name, job title, team, direct line. */
import { useEffect, useState } from "react";
import { Loader2, PenLine, Save } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { getMySignoff, saveMySignoff } from "@/lib/api";
import { sanitizedHtmlProps } from "@/lib/sanitizeEmailHtml";
import { invalidateSignoffCache } from "@/pages/inbox/InboxActions";

export function SignoffCard() {
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getMySignoff().then((r) => { setForm(r.signoff); setPreview(r.preview); }).catch((e) => toast.error(e.response?.data?.detail || e.message)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await saveMySignoff(form);
      setForm(r.signoff); setPreview(r.preview); invalidateSignoffCache();
      toast.success("Sign-off saved — it's added to your next email");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  };

  return (
    <Card data-testid="signoff-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><PenLine className="w-4 h-4 text-blue-600" /> Email sign-off</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Added under every email you send from the portal (inbox replies, forwards, new emails and Customer File emails).
          The SimplyFunded company signature is still appended by CodeTwo — this is just you.
        </p>
        {!form ? <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div> : (
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Field label="Closing"><Input value={form.closing} onChange={(e) => setForm({ ...form, closing: e.target.value })} className="h-8 text-sm" data-testid="signoff-closing" /></Field>
              <Field label="Job title"><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="e.g. Collections Team Leader" className="h-8 text-sm" data-testid="signoff-job-title" /></Field>
              <Field label="Direct line"><Input value={form.direct_line} onChange={(e) => setForm({ ...form, direct_line: e.target.value })} placeholder="e.g. 0203 000 0000" className="h-8 text-sm" data-testid="signoff-direct-line" /></Field>
              <Field label="Team line">
                <select value={form.team_mode || "directory"} onChange={(e) => setForm({ ...form, team_mode: e.target.value })}
                  className="h-8 text-sm rounded-md border border-input bg-white px-2 flex-1" data-testid="signoff-team-mode">
                  <option value="directory">{form.team_display ? `My team from User Management — ${form.team_display}` : "My team from User Management (none set — uses the mailbox team)"}</option>
                  <option value="custom">Type my own…</option>
                  <option value="none">No team line</option>
                </select>
              </Field>
              {form.team_mode === "custom" && (
                <Field label=""><Input value={form.team_label || ""} onChange={(e) => setForm({ ...form, team_label: e.target.value })} placeholder="e.g. Senior Leadership Team" className="h-8 text-sm" data-testid="signoff-team-label" /></Field>
              )}
              {form.team_mode !== "custom" && form.team && (
                <p className="text-[10px] text-muted-foreground" data-testid="signoff-directory-team">User Management has you in <b>{form.team}</b>{form.team_display ? ` → "${form.team_display}"` : ""}. Change it there if that's wrong.</p>
              )}
              <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
                <Switch checked={form.enabled !== false} onCheckedChange={(v) => setForm({ ...form, enabled: v })} className="scale-75" data-testid="signoff-enabled" />
                Add my sign-off to portal emails
              </label>
              <Button size="sm" onClick={save} disabled={saving} data-testid="signoff-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save sign-off
              </Button>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Preview (saved version)</div>
              <div className="border rounded bg-white p-3 min-h-[120px] text-[13px]" data-testid="signoff-preview">
                {preview?.html ? <div dangerouslySetInnerHTML={sanitizedHtmlProps(preview.html)} /> : <span className="text-xs text-muted-foreground italic">Sign-off switched off — emails end with the CodeTwo company signature only.</span>}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">The company name and logo are added by CodeTwo underneath, so they're not repeated here.</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-24 shrink-0">{label}</span>
      {children}
    </div>
  );
}
